import { z } from 'zod';
import { sessionPolicySchema } from './session-policy';

// Schema interno (non-webhook): NON usiamo `.strict()` di default per evitare
// regressioni — payload pre-esistenti con campi extra (es. flag client legacy)
// continuano ad essere accettati. Si valida solo che i campi obbligatori
// abbiano il tipo/forma corretti.
export const loginBodySchema = z.object({
  email: z.string().min(1, 'Identificativo obbligatorio').max(255).trim(),
  password: z.string().min(1, 'Password obbligatoria').max(128),
  forceLogin: z.boolean().optional(),
  totpCode: z.string().length(6).regex(/^\d{6}$/).optional(),
  backupCode: z.string().min(1).max(20).optional(),
}).passthrough();
export type LoginBody = z.infer<typeof loginBodySchema>;

export const refreshTokenBodySchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token obbligatorio').max(2048),
}).passthrough();
export type RefreshTokenBody = z.infer<typeof refreshTokenBodySchema>;

export const refreshTokenResponseDataSchema = z.object({
  token: z.string().min(1),
  refreshToken: z.string().min(1).optional(),
  expiresIn: z.number().int().positive(),
  sessionPolicy: sessionPolicySchema,
});

/**
 * Risposta di `/:tenantSlug/refresh-token` e `/master/refresh-token`: nuovo
 * access token, refresh token ruotato e policy di sessione corrente (così una
 * modifica dal master raggiunge gli utenti già loggati al primo rinnovo).
 */
export const refreshTokenResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    token: z.string().min(1),
    refreshToken: z.string().min(1).optional(),
    expiresIn: z.number().optional(),
    sessionPolicy: sessionPolicySchema.optional(),
  }).passthrough(),
}).passthrough();
export type RefreshTokenResponse = z.infer<typeof refreshTokenResponseSchema>;

// Policy "standard" (reset password self-service): min 8 + complessità.
const passwordPolicy = z.string()
  .min(8, 'La password deve avere almeno 8 caratteri')
  .max(128, 'La password non può superare 128 caratteri')
  .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
  .regex(/[a-z]/, 'La password deve contenere almeno una lettera minuscola')
  .regex(/[0-9]/, 'La password deve contenere almeno un numero')
  .regex(/[^A-Za-z0-9]/, 'La password deve contenere almeno un carattere speciale');

// Policy "rinforzata" per `force-change-password`: 12+ caratteri.
// Specifica storica della rotta `/:tenantSlug/force-change-password` che
// gestisce reset obbligatori imposti dall'admin: requisiti più severi.
const strongPasswordPolicy = z.string()
  .min(12, 'La password deve avere almeno 12 caratteri')
  .max(128, 'La password non può superare 128 caratteri')
  .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
  .regex(/[a-z]/, 'La password deve contenere almeno una lettera minuscola')
  .regex(/[0-9]/, 'La password deve contenere almeno un numero')
  .regex(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/, 'La password deve contenere almeno un carattere speciale');

export const resetPasswordBodySchema = z.object({
  token: z.string().min(1, 'Token obbligatorio').max(512),
  password: passwordPolicy,
}).passthrough();
export type ResetPasswordBody = z.infer<typeof resetPasswordBodySchema>;

export const forgotPasswordBodySchema = z.object({
  email: z.string().email('Formato email non valido').max(255),
}).passthrough();
export type ForgotPasswordBody = z.infer<typeof forgotPasswordBodySchema>;

export const registerBodySchema = z.object({
  email: z.string().email('Formato email non valido').max(255).trim().toLowerCase(),
  password: passwordPolicy,
  firstName: z.string().min(1).max(100).trim(),
  lastName: z.string().min(1).max(100).trim(),
}).passthrough();
export type RegisterBody = z.infer<typeof registerBodySchema>;

// Force-change-password (post-login obbligatorio quando `must_change_password`).
// Token JWT temporaneo con purpose='force_password_change'. Usa la policy
// rinforzata (min 12 + complessità) per allinearsi al baseline storico
// della rotta — NON ridurre questi requisiti senza approval esplicita.
export const forceChangePasswordBodySchema = z.object({
  tempToken: z.string().min(1, 'Token temporaneo obbligatorio').max(2048),
  newPassword: strongPasswordPolicy,
}).passthrough();
export type ForceChangePasswordBody = z.infer<typeof forceChangePasswordBodySchema>;

// Endpoint /verify-token: accetta token tramite Authorization header oppure
// body legacy; manteniamo il body opzionale e .passthrough() per coerenza.
export const verifyTokenBodySchema = z.object({
  token: z.string().min(1).max(4096).optional(),
}).passthrough();
export type VerifyTokenBody = z.infer<typeof verifyTokenBodySchema>;

// Tenant slug param: usato come param-validator riusabile su tutte le rotte
// `/api/:tenantSlug/...`.
export const tenantSlugParamsSchema = z.object({
  tenantSlug: z.string()
    .min(1, 'Slug tenant obbligatorio')
    .max(80)
    .regex(/^[a-z0-9-]+$/i, 'Slug tenant non valido'),
});
export type TenantSlugParams = z.infer<typeof tenantSlugParamsSchema>;

export type RefreshTokenResponseData = z.infer<typeof refreshTokenResponseDataSchema>;

export const bffRefreshBodySchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token obbligatorio').max(2048),
});

export type BffRefreshBody = z.infer<typeof bffRefreshBodySchema>;
